import { sseService } from '@ai/common/src/api/services/sseService';
import { sseStore } from '@ai/common/src/store/SseStore';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';
import { useEffect } from 'react';
import { activeDialogsStore } from '@store/ActiveDialogsStore';
import { ActiveDialogSnackbar } from '@sections/active-dialogs/ActiveDialogSnackbar';
import { DialogMessageRole } from '@ai/common/src/types/chat';
import { widgetService } from '@ai/widget/src/api/services/widget.service';
import { getUserStore } from '@ai/common/src/store/UserStore';

export const sendBrowserNotification = (
  title: string,
  body: string,
  options?: { icon?: string; onClick?: () => void },
) => {
  if (Notification.permission !== 'granted') return;
  const notification = new Notification(title, { body, icon: options?.icon });
  if (options?.onClick) {
    notification.onclick = () => {
      window.focus();
      options.onClick!();
      notification.close();
    };
  }
};

export const BrowserNotificationsProvider = observer(() => {
  const { enqueueSnackbar } = useSnackbar();
  const { operatorRequestedEvent, isConnected, connectionId, dialogMessageEvent } = sseStore;
  const { currentUser } = getUserStore('platform');

  useEffect(() => {
    if (isConnected && !!currentUser) {
      sseService.subscribeToActiveDialogs(connectionId);
    }
  }, [isConnected, connectionId, currentUser]);

  useEffect(() => {
    if (!operatorRequestedEvent) return;

    const title = 'Новый запрос оператора';
    const body = `Эксперт: ${operatorRequestedEvent.expert_name}`;
    const dialogId = operatorRequestedEvent.dialog_id;

    if (document.hidden) {
      sendBrowserNotification(title, body, {
        icon: `${widgetService.rootOrigin}${operatorRequestedEvent.expert_image_id}`,
        onClick: () => {
          activeDialogsStore.setPendingDialogId(dialogId);
          window.location.hash = '#/active-dialogs';
        },
      });
      return;
    }

    if (window.location.hash.includes('/active-dialogs')) return;

    enqueueSnackbar('', {
      content: (key) => (
        <ActiveDialogSnackbar
          snackbarKey={key}
          expertImageId={operatorRequestedEvent.expert_image_id}
          expertName={operatorRequestedEvent.expert_name}
          title={title}
          dialogId={dialogId}
        />
      ),
      autoHideDuration: null,
    });
  }, [operatorRequestedEvent, enqueueSnackbar]);

  useEffect(() => {
    const processDialogMessageEvent = async () => {
      if (!dialogMessageEvent) return;

      const { dialog_message, dialog_mode, expert_name, expert_image_id } = dialogMessageEvent;

      if (dialog_message.role !== DialogMessageRole.USER) return;
      if (dialog_mode !== 'supervisor') return;

      const isSupervisorOnline = await activeDialogsStore.getIsSupervisorOnline();
      if (!isSupervisorOnline) return;

      const title = 'Новое сообщение';
      const messageText = dialog_message.message;
      const dialogId = dialog_message.dialog_id;

      if (document.hidden) {
        sendBrowserNotification(title, `Эксперт: ${expert_name}\n${messageText.slice(0, 100)}`, {
          icon: `${widgetService.rootOrigin}${expert_image_id}`,
          onClick: () => {
            activeDialogsStore.setPendingDialogId(dialogId);
            window.location.hash = '#/active-dialogs';
          },
        });
        return;
      }

      if (window.location.hash.includes('/active-dialogs')) return;

      enqueueSnackbar('', {
        content: (key) => (
          <ActiveDialogSnackbar
            snackbarKey={key}
            expertImageId={expert_image_id}
            expertName={expert_name}
            title={title}
            message={messageText}
            dialogId={dialogId}
          />
        ),
        autoHideDuration: null,
      });
    };

    void processDialogMessageEvent();
  }, [dialogMessageEvent, enqueueSnackbar]);

  return null;
});
